import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { monthlyTimesheetsAPI } from "../services/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";

const TEN_MIN = 10 * 60 * 1000;

const DEFAULT_START = "09:30";
const DEFAULT_END = "19:00";
const DEFAULT_BREAK_MINUTES = 90;

function ymd(d) {
  return d.toISOString().split("T")[0];
}

function pad(n) {
  return String(n).padStart(2, "0");
}

function currentTimeHHMM() {
  const now = new Date();
  return `${pad(now.getHours())}:${pad(now.getMinutes())}`;
}

function formatDatePT(dateStr) {
  if (!dateStr) return "";
  try {
    const d = new Date(`${dateStr}T12:00:00`);
    return d.toLocaleDateString("pt-PT");
  } catch {
    return dateStr;
  }
}

function firstName(name) {
  return String(name || "").trim().split(/\s+/)[0] || "Olá";
}


function isWeekday(d) {
  const day = d.getDay();
  return day >= 1 && day <= 5;
}

function prevWorkday(d) {
  const x = new Date(d);
  x.setDate(x.getDate() - 1);
  while (!isWeekday(x)) x.setDate(x.getDate() - 1);
  return x;
}

function getDaysInMonth(year, month) {
  return new Date(year, month, 0).getDate();
}

function getEasterSunday(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day, 12, 0, 0);
}

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function isSameYMD(a, b) {
  return ymd(a) === ymd(b);
}

function isPortugueseCustomHoliday(dateObj) {
  const year = dateObj.getFullYear();
  const easter = getEasterSunday(year);
  const goodFriday = addDays(easter, -2);
  const easterMonday = addDays(easter, 1);
  const carnival = addDays(easter, -47);

  const fixed = [
    "01-01",
    "04-25",
    "05-01",
    "06-10",
    "06-13",
    "08-15",
    "10-05",
    "11-01",
    "12-01",
    "12-08",
    "12-24",
    "12-25",
    "12-31",
  ];

  const md = `${pad(dateObj.getMonth() + 1)}-${pad(dateObj.getDate())}`;
  if (fixed.includes(md)) return true;
  if (isSameYMD(dateObj, goodFriday)) return true;
  if (isSameYMD(dateObj, easterMonday)) return true;
  if (isSameYMD(dateObj, carnival)) return true;

  return false;
}

function shouldPrefillDay(year, month, day) {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1;
  const currentDay = today.getDate();

  if (year < currentYear) return true;
  if (year > currentYear) return false;
  if (month < currentMonth) return true;
  if (month > currentMonth) return false;
  return day <= currentDay;
}

function makeEntry(dateStr, prefill = false, forceHoliday = false) {
  const dateObj = new Date(`${dateStr}T12:00:00`);
  const isHoliday = forceHoliday || isPortugueseCustomHoliday(dateObj);
  const weekday = isWeekday(dateObj);

  if (isHoliday) {
    return {
      date: dateStr,
      start_time: "",
      end_time: "",
      break_minutes: DEFAULT_BREAK_MINUTES,
      overtime_minutes: 0,
      absence_type: "feriado",
      absence_hours: 8,
      notes: "",
      absence_override: false,
      start_confirmed: true,
      end_confirmed: true,
    };
  }

  return {
    date: dateStr,
    start_time: prefill && weekday ? DEFAULT_START : "",
    end_time: prefill && weekday ? DEFAULT_END : "",
    break_minutes: DEFAULT_BREAK_MINUTES,
    overtime_minutes: 0,
    absence_type: "",
    absence_hours: 0,
    notes: "",
    absence_override: false,
    start_confirmed: false,
    end_confirmed: false,
  };
}

function buildMonthEntries(year, month) {
  const days = getDaysInMonth(year, month);
  const entries = [];

  for (let day = 1; day <= days; day++) {
    const dateStr = `${year}-${pad(month)}-${pad(day)}`;
    const dateObj = new Date(`${dateStr}T12:00:00`);
    const prefill = isWeekday(dateObj) && shouldPrefillDay(year, month, day);

    entries.push(makeEntry(dateStr, prefill, isPortugueseCustomHoliday(dateObj)));
  }

  return entries;
}

function entryIsFullAbsence(e) {
  const abType = String(e?.absence_type || "").trim();
  const abH = Number(e?.absence_hours || 0);
  return !!abType && abH >= 8;
}

function upsertEntry(entries, dateStr, updater) {
  const next = Array.isArray(entries) ? [...entries] : [];
  const idx = next.findIndex((x) => x?.date === dateStr);

  if (idx === -1) {
    const fresh = makeEntry(dateStr, false);
    updater(fresh);
    next.push(fresh);
  } else {
    const copy = { ...next[idx] };
    updater(copy);
    next[idx] = copy;
  }

  return next;
}

export default function WorkdayConfirmDialog() {
  const { user } = useAuth();

  const employeeId = user?.employee_id || null;
  const hasEmployeeProfile = !!employeeId;
  const userFirstName = firstName(user?.name);

  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [needTodayIn, setNeedTodayIn] = useState(false);
  const [needTodayOut, setNeedTodayOut] = useState(false);
  const [needPrevOut, setNeedPrevOut] = useState(false);

  const [todayInManual, setTodayInManual] = useState("");
  const [todayOutManual, setTodayOutManual] = useState("");
  const [prevOutManual, setPrevOutManual] = useState("");

  const [showManualTodayIn, setShowManualTodayIn] = useState(false);
  const [showManualTodayOut, setShowManualTodayOut] = useState(false);
  const [showManualPrevOut, setShowManualPrevOut] = useState(false);

  const [todayTsId, setTodayTsId] = useState(null);
  const [prevTsId, setPrevTsId] = useState(null);

  const [targets, setTargets] = useState({
    today: "",
    prev: "",
    todayYear: null,
    todayMonth: null,
    prevYear: null,
    prevMonth: null,
  });

  const [snoozeUntil, setSnoozeUntil] = useState(0);

  const todayLabel = formatDatePT(targets.today);
  const prevLabel = formatDatePT(targets.prev);

  const subtitle =
    needTodayIn
      ? todayLabel ? `Falta confirmar a entrada de ${todayLabel}.` : "Falta confirmar a entrada de hoje."
      : needPrevOut
        ? "Ainda falta registar a saída do último dia útil."
        : needTodayOut
          ? prevLabel ? `Falta confirmar a saída de ${prevLabel}.` : "Falta confirmar a saída de hoje."
          : "Está tudo confirmado por agora.";

  const isTodayHoliday = useMemo(() => {
    const now = new Date();
    return isPortugueseCustomHoliday(now) || !isWeekday(now);
  }, []);


  const ensureMonthlyTimesheet = useCallback(async (year, month) => {
    const res = await monthlyTimesheetsAPI.getAll({ employee_id: employeeId, year, month });
    const arr = Array.isArray(res?.data) ? res.data : [];
    if (arr.length > 0) return arr[0];

    const created = await monthlyTimesheetsAPI.create({
      employee_id: employeeId,
      year,
      month,
      entries: buildMonthEntries(year, month),
    });

    return created.data;
  }, [employeeId]);

  const checkMissingAndMaybeOpen = useCallback(async () => {
    try {
      if (!hasEmployeeProfile || !employeeId) return;
      if (Date.now() < snoozeUntil) return;

      const now = new Date();
      const todayDate = new Date();
      const prevDateObj = prevWorkday(now);

      const today = ymd(todayDate);
      const prev = ymd(prevDateObj);

      const todayYear = todayDate.getFullYear();
      const todayMonth = todayDate.getMonth() + 1;
      const prevYear = prevDateObj.getFullYear();
      const prevMonth = prevDateObj.getMonth() + 1;

      const todayTs = await ensureMonthlyTimesheet(todayYear, todayMonth);
      const prevTs =
        todayYear === prevYear && todayMonth === prevMonth
          ? todayTs
          : await ensureMonthlyTimesheet(prevYear, prevMonth);

      setTodayTsId(todayTs?.id || null);
      setPrevTsId(prevTs?.id || null);

      setTargets({
        today,
        prev,
        todayYear,
        todayMonth,
        prevYear,
        prevMonth,
      });

      const todayEntries = Array.isArray(todayTs?.entries) ? [...todayTs.entries] : [];
      const prevEntries = todayTs?.id === prevTs?.id
        ? todayEntries
        : (Array.isArray(prevTs?.entries) ? [...prevTs.entries] : []);

      let eToday = todayEntries.find((x) => x?.date === today);
      let ePrev = prevEntries.find((x) => x?.date === prev);

      if (!eToday) eToday = makeEntry(today, false, isTodayHoliday);
      if (!ePrev) ePrev = makeEntry(prev, false);

      const prevLooksWorked = !!String(ePrev.start_time || "").trim() || !!ePrev.start_confirmed;
      const askPrevOut = prevLooksWorked && !ePrev.end_confirmed && !entryIsFullAbsence(ePrev);

      const askTodayIn =
        !isTodayHoliday &&
        !eToday.start_confirmed &&
        !entryIsFullAbsence(eToday);

      const askTodayOut =
        !isTodayHoliday &&
        !!String(eToday.start_time || "").trim() &&
        !!eToday.start_confirmed &&
        !eToday.end_confirmed &&
        !entryIsFullAbsence(eToday);

      setNeedPrevOut(askPrevOut);
      setNeedTodayIn(askTodayIn && !askPrevOut);
      setNeedTodayOut(askTodayOut && !askPrevOut && !askTodayIn);

      setTodayInManual(String(eToday.start_time || DEFAULT_START));
      setTodayOutManual(String(eToday.end_time || DEFAULT_END));
      setPrevOutManual(String(ePrev.end_time || DEFAULT_END));

      if (askPrevOut || askTodayIn || askTodayOut) {
        setOpen(true);
      }
    } catch (e) {
      console.error("WorkdayConfirmDialog check error:", e);
    }
  }, [hasEmployeeProfile, employeeId, snoozeUntil, ensureMonthlyTimesheet, isTodayHoliday]);

  useEffect(() => {
    checkMissingAndMaybeOpen();
    const t = setInterval(checkMissingAndMaybeOpen, TEN_MIN);
    return () => clearInterval(t);
  }, [checkMissingAndMaybeOpen]);

  async function savePrevOut(value) {
    if (!prevTsId) {
      toast.error("Não encontrei a folha do último dia útil.");
      return;
    }

    const prev = targets.prev;
    const ts = (await monthlyTimesheetsAPI.getById(prevTsId)).data;
    const entries = upsertEntry(ts?.entries || [], prev, (e) => {
      e.end_time = String(value).trim();
      e.end_confirmed = true;
    });

    await monthlyTimesheetsAPI.update(prevTsId, { entries });
  }

  async function saveTodayIn(value) {
    if (!todayTsId) {
      toast.error("Não encontrei a folha do mês atual.");
      return;
    }

    const today = targets.today;
    const ts = (await monthlyTimesheetsAPI.getById(todayTsId)).data;
    const entries = upsertEntry(ts?.entries || [], today, (e) => {
      e.start_time = String(value).trim();
      e.break_minutes = Number(e.break_minutes ?? DEFAULT_BREAK_MINUTES) || DEFAULT_BREAK_MINUTES;
      e.start_confirmed = true;
      if (!e.absence_type) e.absence_hours = 0;
    });

    await monthlyTimesheetsAPI.update(todayTsId, { entries });
  }

  async function saveTodayOut(value) {
    if (!todayTsId) {
      toast.error("Não encontrei a folha do mês atual.");
      return;
    }

    const today = targets.today;
    const ts = (await monthlyTimesheetsAPI.getById(todayTsId)).data;
    const entries = upsertEntry(ts?.entries || [], today, (e) => {
      e.end_time = String(value).trim();
      e.break_minutes = Number(e.break_minutes ?? DEFAULT_BREAK_MINUTES) || DEFAULT_BREAK_MINUTES;
      e.end_confirmed = true;
    });

    await monthlyTimesheetsAPI.update(todayTsId, { entries });
  }

  async function handleConfirmNow() {
    setSaving(true);
    try {
      if (needPrevOut) {
        await savePrevOut(currentTimeHHMM());
        toast.success("Saída do último dia útil confirmada.");
      } else if (needTodayIn) {
        await saveTodayIn(currentTimeHHMM());
        toast.success("Entrada de hoje confirmada.");
      } else if (needTodayOut) {
        await saveTodayOut(currentTimeHHMM());
        toast.success("Saída de hoje confirmada.");
      }

      setOpen(false);
      setShowManualPrevOut(false);
      setShowManualTodayIn(false);
      setShowManualTodayOut(false);

      setTimeout(() => {
        checkMissingAndMaybeOpen();
      }, 400);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Erro ao confirmar ponto.");
    } finally {
      setSaving(false);
    }
  }

  async function handleConfirmManual() {
    setSaving(true);
    try {
      if (needPrevOut) {
        if (!String(prevOutManual || "").trim()) {
          toast.error("Indica a hora de saída do último dia útil.");
          return;
        }
        await savePrevOut(prevOutManual);
        toast.success("Saída do último dia útil guardada.");
      } else if (needTodayIn) {
        if (!String(todayInManual || "").trim()) {
          toast.error("Indica a hora de entrada.");
          return;
        }
        await saveTodayIn(todayInManual);
        toast.success("Entrada de hoje guardada.");
      } else if (needTodayOut) {
        if (!String(todayOutManual || "").trim()) {
          toast.error("Indica a hora de saída.");
          return;
        }
        await saveTodayOut(todayOutManual);
        toast.success("Saída de hoje guardada.");
      }

      setOpen(false);
      setShowManualPrevOut(false);
      setShowManualTodayIn(false);
      setShowManualTodayOut(false);

      setTimeout(() => {
        checkMissingAndMaybeOpen();
      }, 400);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Erro ao guardar ponto.");
    } finally {
      setSaving(false);
    }
  }

  function ignoreFor10Min() {
    setOpen(false);
    setSnoozeUntil(Date.now() + TEN_MIN);
  }

  if (!hasEmployeeProfile) return null;

  const title =
    needTodayIn
      ? `Bom dia, ${userFirstName}.`
      : needPrevOut
        ? `${userFirstName}, falta um registo.`
        : needTodayOut
          ? `${userFirstName}, falta fechar o dia de ${prevLabel || todayLabel || "hoje"}.`
          : `Olá, ${userFirstName}.`;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) ignoreFor10Min(); else setOpen(true); }}>
      <DialogContent className="bg-slate-900 border-slate-800 w-[95vw] max-w-2xl p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-slate-800">
          <DialogTitle className="text-white text-2xl font-semibold">{title}</DialogTitle>
        </DialogHeader>
        
        <div className="px-6 pt-4 pb-1">
          <p className="text-slate-300 text-sm">{subtitle}</p>
        </div>

<div className="px-6 py-5 space-y-5">
          {needPrevOut && (
            <div className="rounded-xl border border-slate-800 bg-slate-800/40 p-4 space-y-3">
              <p className="text-sm text-slate-300">
                Falta confirmar a <strong>saída do último dia útil</strong>.
              </p>
              <p className="text-xs text-slate-500">
                Horário habitual de saída: {DEFAULT_END}
              </p>

              {showManualPrevOut && (
                <div>
                  <Label className="text-slate-300">Hora de saída</Label>
                  <Input
                    type="time"
                    value={prevOutManual}
                    onChange={(e) => setPrevOutManual(e.target.value)}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
              )}
            </div>
          )}

          {needTodayIn && (
            <div className="rounded-xl border border-slate-800 bg-slate-800/40 p-4 space-y-3">
              <p className="text-sm text-slate-300">
                Regista agora a <strong>entrada de {todayLabel || "hoje"}</strong>.
              </p>
              <p className="text-xs text-slate-500">
                Horário habitual: {DEFAULT_START} às {DEFAULT_END}, com almoço 13:00 às 14:30.
              </p>

              {showManualTodayIn && (
                <div>
                  <Label className="text-slate-300">Hora de entrada</Label>
                  <Input
                    type="time"
                    value={todayInManual}
                    onChange={(e) => setTodayInManual(e.target.value)}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
              )}
            </div>
          )}

          {needTodayOut && (
            <div className="rounded-xl border border-slate-800 bg-slate-800/40 p-4 space-y-3">
              <p className="text-sm text-slate-300">
                Regista agora a <strong>saída de {prevLabel || todayLabel || "hoje"}</strong>.
              </p>
              <p className="text-xs text-slate-500">
                Horário habitual de saída: {DEFAULT_END}
              </p>

              {showManualTodayOut && (
                <div>
                  <Label className="text-slate-300">Hora de saída</Label>
                  <Input
                    type="time"
                    value={todayOutManual}
                    onChange={(e) => setTodayOutManual(e.target.value)}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="px-6 py-4 border-t border-slate-800 bg-slate-900 flex flex-col sm:flex-row sm:flex-wrap sm:justify-end gap-3">
          <Button variant="outline" onClick={ignoreFor10Min} disabled={saving} className="border-slate-700 min-w-[170px]">
            Lembrar mais tarde
          </Button>

          {needPrevOut && !showManualPrevOut && (
            <>
              <Button variant="outline" onClick={() => setShowManualPrevOut(true)} disabled={saving} className="border-slate-700 min-w-[220px]">
                Corrigir hora manualmente
              </Button>
              <Button onClick={handleConfirmNow} disabled={saving} className="min-w-[170px] bg-emerald-500 hover:bg-emerald-600">
                {saving ? "A guardar..." : "Dar saída agora"}
              </Button>
            </>
          )}

          {needTodayIn && !showManualTodayIn && (
            <>
              <Button variant="outline" onClick={() => setShowManualTodayIn(true)} disabled={saving} className="border-slate-700 min-w-[220px]">
                Corrigir hora manualmente
              </Button>
              <Button onClick={handleConfirmNow} disabled={saving} className="min-w-[170px] bg-emerald-500 hover:bg-emerald-600">
                {saving ? "A guardar..." : "Dar entrada agora"}
              </Button>
            </>
          )}

          {needTodayOut && !showManualTodayOut && (
            <>
              <Button variant="outline" onClick={() => setShowManualTodayOut(true)} disabled={saving} className="border-slate-700 min-w-[220px]">
                Corrigir hora manualmente
              </Button>
              <Button onClick={handleConfirmNow} disabled={saving} className="min-w-[170px] bg-emerald-500 hover:bg-emerald-600">
                {saving ? "A guardar..." : "Dar saída agora"}
              </Button>
            </>
          )}

          {(showManualPrevOut || showManualTodayIn || showManualTodayOut) && (
            <Button onClick={handleConfirmManual} disabled={saving} className="min-w-[220px] bg-emerald-500 hover:bg-emerald-600">
              {saving ? "A guardar..." : "Guardar hora manual"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
